/**
 * useSaveState — the single shared source of a saveable's saved state (SV-C1).
 *
 * The bug this replaces: every SaveButton held its own `useState(initialSaved)`
 * with a local optimistic toggle and NO shared store or cache invalidation. The
 * search card and the map popup for the same listing were two React trees each
 * seeded from `listing.saved`, so saving from one did not update the other, and
 * a background refetch could overwrite the optimistic heart with stale data.
 *
 * Now the optimistic value lives in ONE TanStack Query cache slot per saveable
 * (`queryKeys.saved.overlay`), written only by this mutation. Every SaveButton
 * for the same entity subscribes to that slot, so their hearts are the same
 * value by construction — no prop-drilled `initialSaved`, no per-button state,
 * no effect syncing two sources. The displayed value is the overlay when set,
 * else the server truth (`serverSaved`) the caller already holds on the card.
 *
 * Lives in `shared/hooks/` (not `shared/lib/`): it is React, and `shared/lib`
 * is framework-agnostic by rule (Domain Purity). The pure SaveableRef/key types
 * it consumes stay in `shared/lib/saved`.
 */
import { localizedToast } from '@/shared/i18n/toast';
import { useLocalizeApiError } from '@/shared/i18n/useLocalizeApiError';
import { bff } from '@/shared/lib/bff';
import { queryKeys } from '@/shared/lib/query-keys';
import { savedKey } from '@/shared/lib/saved/key';
import type { SaveableRef } from '@/shared/lib/saved/save-ref';
import { bffError } from '@letsgethome/lib/server-errors';
import { saveableId } from '@letsgethome/types/branded';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export interface SaveState {
  /** The saved value every SaveButton for this entity agrees on. */
  readonly saved: boolean;
  /** Toggle save/unsave; re-entry is guarded via `isPending`. */
  readonly toggle: () => void;
  /** A save/unsave request is in flight. */
  readonly isPending: boolean;
}

/**
 * @param saveable    the entity behind the heart.
 * @param serverSaved the authoritative server value the caller holds (a card's
 *                     `saved`), read every render as the overlay's fallback —
 *                     NOT a one-time seed.
 */
export function useSaveState(saveable: SaveableRef, serverSaved: boolean): SaveState {
  const queryClient = useQueryClient();
  const localizeApiError = useLocalizeApiError();
  const key = savedKey(saveable);

  // A write-only shared slot: `skipToken` means it never fetches, but every
  // SaveButton for this entity subscribes to it, so a `setQueryData` from any
  // instance re-renders them all. `undefined` = no override yet → server truth.
  const { data: override } = useQuery<boolean, Error, boolean>({
    queryKey: queryKeys.saved.overlay(key),
    queryFn: skipToken,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const saved = override ?? serverSaved;

  const { mutate, isPending } = useMutation({
    mutationFn: async (next: boolean) => {
      // Explicit polymorphic crossing: the branded id is minted into the
      // polymorphic SaveableId at this boundary, never implicitly.
      const body = { saveableId: saveableId(saveable.id), saveableType: saveable.kind };
      const result = next
        ? await bff('/api/saved/create', body)
        : await bff('/api/saved/delete', body);
      if (!result.success) throw bffError(result.error, 'Failed');
    },
    onMutate: (next: boolean) => {
      const previous = queryClient.getQueryData<boolean>(queryKeys.saved.overlay(key));
      queryClient.setQueryData<boolean>(queryKeys.saved.overlay(key), next);
      return { previous };
    },
    onError: (err, _next, context) => {
      // Revert the optimistic write. `setQueryData(key, undefined)` BAILS OUT in
      // TanStack v5 (an undefined result is "no change"), so when there was no
      // prior override we must remove the slot for the read to fall back to the
      // server value — a plain setQueryData would leave the failed heart lit.
      if (context?.previous === undefined) {
        queryClient.removeQueries({ queryKey: queryKeys.saved.overlay(key), exact: true });
      } else {
        queryClient.setQueryData<boolean>(queryKeys.saved.overlay(key), context.previous);
      }
      // SV-C2: a failed save used to revert SILENTLY — the heart just snapped
      // back with no explanation. Surface the typed server error through the
      // app's localized-toast path.
      localizedToast.error(localizeApiError(err));
    },
  });

  // Memoized so the returned object holds only stable identifiers
  // (stable-hook-return): a fresh closure here would churn any consumer that
  // lists `toggle` in a dependency array.
  const toggle = useCallback(() => mutate(!saved), [mutate, saved]);

  return { saved, toggle, isPending };
}
